/**
 * useShellForecast - the odds, the turn as it stands, and the firing solutions.
 *
 * - The PROBABILITY plot must match the roll: the same condition mods, the
 *   same gate, the same ground underfoot.
 * - THE TURN CARD's intent (v8.322.0): the turn projected from state the
 *   shell already holds; the card owns nothing. Its headline is also the
 *   map's FIRING LINE chip.
 * - The FIRING SOLUTIONS board (v8.250.0): the same inputs, plus the called
 *   shot's state so the hook can back its chip out of the modifier.
 *
 * Returns `{ probability, intent, firingLine, firingSolutions }`.
 * Extracted from DeathWatchSimulator.
 */
import { useMemo } from 'react';
import { isDowned } from '../engine/conditions';
import { turnIntent } from '../engine/turnIntent';
import useProbability from './useProbability';
import useFiringSolutions from './useFiringSolutions';

/** What the map measured, as the card reads it; null with no measurement. */
function measurementOf(m) {
  if (!m) return null;
  return { metres: m.metres, rangeBand: m.rangeBand, cover: m.cover ?? null, blocked: !!m.blocked };
}

/** The intent's inputs, from the shell's state. */
export function intentInputs(a) {
  const { slot, currentTab, probabilityResult: odds } = a;
  const armed = !!slot && !slot.isEmpty;
  return {
    actorName: a.actor?.name ?? null,
    weapon: armed ? { name: slot.weaponName, isMelee: a.isMelee } : null,
    fireModeLabel: a.fireModes.find((m) => m.key === a.fireMode)?.label ?? null,
    chips: a.ledgerChips.map(({ id, label, value, source }) => ({ id, label, value, source })),
    target: currentTab
      ? { name: currentTab.target?.name || currentTab.label, down: isDowned(currentTab.target?.conditions) }
      : null,
    measurement: measurementOf(a.measurement),
    odds: odds ? { hitChance: odds.hits.hitChance, meanDamage: odds.damage.mean } : null,
    triggerLabel: a.attackTriggerLabel,
    canFire: armed && !a.isLocked('attack') && !a.pendingAttack,
  };
}

/** The firing line's chip: the card's headline, on the map; null with nothing to fire at. */
export function firingLineOf(intent) {
  if (!intent.weapon || !intent.targetName) return null;
  const parts = [intent.weapon.toUpperCase()];
  if (intent.mode) parts.push(intent.mode.toUpperCase());
  if (intent.odds) parts.push(intent.odds.hit);
  return { label: parts.join(' · '), blocked: intent.verdict === 'LINE OF SIGHT BLOCKED' };
}

/** The inputs the plot and the board share. */
function oddsInputs(a) {
  const t = a.attackToggles;
  return {
    slot: a.effectiveSlot,
    unnaturalDosMode: a.unnaturalDosMode,
    attackerConditions: a.actorProjection,
    applyConditionMods: a.applyConditionModifiers,
    condOptions: a.condModOptions,
    target: a.currentTab?.target || null,
    attackMod: a.attackModFinal,
    rangeBand: a.rangeBand,
    fireMode: a.fireMode,
    isMelee: a.isMelee,
    hatred: a.hatred,
    frenzied: a.frenzied,
    attackerCharStats: a.attackerCharStats,
    aimed: !!(t.aimHalf || t.aimFull),
    charge: !!t.charge,
    rerollDamage: !!a.rerollDamage,
    targetLocation: a.targetLocation,
  };
}

/** The card's intent, rebuilt only when what it shows can change (over length: bindings only). */
function useIntent(a, probabilityResult) {
  const { actor, slot, isMelee, fireModes, fireMode, ledgerData, currentTab, measurement } = a;
  const { attackTriggerLabel, isLocked, pendingAttack } = a;
  return useMemo(
    () =>
      turnIntent(
        intentInputs({
          actor,
          slot,
          isMelee,
          fireModes,
          fireMode,
          currentTab,
          measurement,
          attackTriggerLabel,
          isLocked,
          pendingAttack,
          probabilityResult,
          ledgerChips: ledgerData.chips,
        }),
      ),
    [
      actor,
      slot,
      isMelee,
      fireModes,
      fireMode,
      ledgerData,
      currentTab,
      measurement,
      probabilityResult,
      attackTriggerLabel,
      isLocked,
      pendingAttack,
    ],
  );
}

/**
 * @param {object} args - the armed slot and its overridden view, the fire
 *   modes and mode, the ledger, the actor and their projection, the current
 *   tab, the live measurement, the trigger label, isLocked, pendingAttack,
 *   and the roll's inputs (rulings, modifiers, band, qualities, toggles,
 *   the called shot's reduction).
 */
export default function useShellForecast(args) {
  const shared = oddsInputs(args);
  const probability = useProbability(shared);
  const intent = useIntent(args, probability.result);
  const firingLine = useMemo(() => firingLineOf(intent), [intent]);
  const firingSolutions = useFiringSolutions({
    ...shared,
    calledShot: !!args.attackToggles.calledShot,
    calledShotReduction: args.calledShotReduction,
  });
  return { probability, intent, firingLine, firingSolutions };
}
